<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="hub.isDiff"
    :isOutdated="isOutdated"
    :itemType="'HUB'"
    :typename="'hub'"
    :item="hub"
    :onUpdate="update"
    :onCreate="create"
    :onDelete="del"
    :noHeader="tab === 'publications'"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <nav
      slot="top-menu"
      class="ui secondary pointing menu"
      id="application-menu"
      v-if="mode === 'edit'"
    >
      <router-link
        class="item capitalize-fl"
        :to="{ name: 'hub', params: { hubId: hub.id, tab: 'details' } }"
        :active-class="'active'"
      >
        <span class="capitalize-fl">{{ $t('CONFIGURATION') }}</span>
      </router-link>
      <router-link
        class="item capitalize-fl"
        :to="{ name: 'hub', params: { hubId: hub.id, tab: 'publications' } }"
        :active-class="'active'"
      >
        <span class="capitalize-fl">{{ $tc('PUBLICATION', 2) }}</span>
      </router-link>
    </nav>

    <fieldset slot="fieldset" :disabled="isOutdated" v-if="tab === 'details'">
      <div
        class="field required"
        :class="{ error: $v.hub.name.$error }"
        v-diff-field.disable="hubDiff.name"
      >
        <label for="hubName">{{ $t('NAME') }}</label>
        <input
          id="hubName"
          type="text"
          :placeholder="$t('NAME')"
          v-model.trim="hub.name"
          @click="$v.hub.name.$touch()"
        />
      </div>

      <div
        class="field required"
        :class="{ error: $v.hub.url.$error }"
        v-diff-field.disable="hubDiff.url"
      >
        <label for="hubUrl">{{ $t('URL') }}</label>
        <input
          id="hubUrl"
          type="text"
          :placeholder="$t('URL')"
          v-model.trim="hub.url"
          @click="$v.hub.url.$touch()"
        />
      </div>

      <div
        class="field required"
        :class="{ error: $v.hub.token.$error }"
        v-diff-field.disable="hubDiff.token"
      >
        <label for="hubToken">{{ $t('TOKEN') }}</label>
        <textarea
          id="hubToken"
          class="monospaced"
          :placeholder="$t('TOKEN')"
          v-model.trim="hub.token"
          @click="$v.hub.token.$touch()"
        />
      </div>
    </fieldset>
    <fieldset slot="fieldset" v-if="tab === 'publications'">
      <h4 class="ui header">{{ $t('PUBLICATIONS_FROM', { hub: this.hub.name }) }}</h4>
      <kitchen-table :page="page" :maxHeight="'100vh - 200px'" :columns="columns">
        <template #name="{ cell, value }">
          <router-link
            v-if="value"
            :to="{
              name: 'app-flow',
              params: { appId: value.application, flowId: value.id, mode: 'hub' }
            }"
          >
            {{ cell }}
          </router-link>
        </template>
        <template #type="{ cell }">
          {{ $t('PUBLICATION_TYPE_' + cell + '_NAME') }}
        </template>
        <template #version="{ value }">
          {{ value }}
        </template>
      </kitchen-table>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import { Application, Hub, Toast, ErrorCodes, UnsavedChecker, Flow } from 'services'
import { MainSection, KitchenTable, KitchenPage, InMemoryPager } from 'ui'
import { take } from 'rxjs/operators'
import { required } from 'vuelidate/lib/validators'

const hub = {
  name: '',
  token: '',
  url: ''
}

export default {
  name: 'hub',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection, KitchenTable },
  props: {
    tab: {
      type: String,
      default: () => 'details'
    }
  },
  data() {
    return {
      mode: '',
      isOutdated: false,
      hub: {},
      hubId: '',
      hubDiff: {},
      publications: [],
      columns: [
        {
          key: 'name',
          name: this.$i18n.t('NAME'),
          value: p => _.find(this.flows, f => f.id === p.origin.identifier),
          sorts: true
        },
        {
          key: 'type',
          name: this.$i18n.t('PUBLICATION_TYPE'),
          sorts: true
        },
        {
          key: 'abstract',
          name: this.$i18n.t('ABSTRACT')
        },
        {
          key: 'version',
          name: this.$i18n.tc('VERSION', 1),
          value: p => (p.releaseNote ? p.releaseNote.version : '')
        }
      ]
    }
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit'
    },

    page() {
      return new KitchenPage(
        new InMemoryPager({
          items: this.publications,
          pageSize: 50
        })
      )
    },

    registryName() {
      return this.$store.state.config.name
    },

    flows() {
      return this.$store.state.flow.items
    },

    autoHub() {
      return { ..._.find(this.$store.state.hub.items, h => h.id === this.hubId) }
    }
  },

  validations: {
    hub: {
      name: {
        required
      },
      token: {
        required
      },
      url: {
        required
      }
    }
  },

  methods: {
    readHub(hubId: string) {
      this.isOutdated = false
    },

    create() {
      if (!this.$v.$invalid) {
        return Hub.save('', this.hub)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('HUB', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'hub',
              params: { hubId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('HUB', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('HUB', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    update() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Hub.save(this.hub.id, this.hub)
            .then(res => {
              this.hub.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('HUB', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('HUB', 1)
                    }),
                    thing: this.$i18n.tc('HUB', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('HUB', 1)
                    })
                  }),
                  { remove: 5000 }
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('HUB', 1) }),
              thing: this.$i18n.tc('HUB', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('HUB', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    del() {
      Hub.del(this.hub.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('HUB', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'hubs'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('HUB', 1) })
            })
          )
          console.error(err)
        })
    },

    editMode() {
      this.mode = 'edit'
      this.hubId = this.$route.params.hubId
    },

    createMode() {
      this.mode = 'create'
      this.hub = { ...Hub.defaultHub }
    },

    onChangeChosen({ changedItem, fullDiff }) {
      this.hub = changedItem
      this.hubDiff = fullDiff
    },

    onRevert(hub) {
      this.hub = hub
      this.hubDiff = {}
    }
  },

  mounted() {
    if (this.$route.params.hubId) {
      Hub.list().then(() => this.editMode())
    } else {
      this.createMode()
      Hub.list()
    }

    Flow.readAllFlows()
  },

  watch: {
    autoHub: function(h) {
      this.hub = h
      if (this.mode === 'edit') {
        Hub.connectionFor(h).then(c => {
          Hub.latest(c).then(response => {
            this.publications = _.filter(
              response.body.items,
              // NOTE: registry name may change if no registry name is specified in Kwanza and it is restarted
              item => item.origin.registry === this.registryName
            )
          })
        })
      }
    },

    '$route.params.hubId': function(newHubId) {
      this.$v.$reset()
      if (newHubId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
